import React, { useCallback, useMemo, useState } from 'react';

import { link, routes } from '@aline/routes';
import { Typography } from '@mui/material';
import type { AlertProps } from '@mui/material';
import { differenceInDays, parseISO } from 'date-fns';
import { useMatch, useNavigate } from 'react-router-dom';
import { AlineAlert, SmallCloseIconButton } from 'src/components';
import { openCheckoutDialog } from 'src/components/admin/billing/checkout-dialog-var';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { alineLinkButtonStylesWithUnderline } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  alertActionContainer: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    gap: theme.spacing(0.5),
  },
  boldAlertText: {
    ...alineLinkButtonStylesWithUnderline,
  },
  container: {
    alignItems: 'center',
    bottom: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    left: '50%',
    pointerEvents: 'none',
    position: 'absolute',
    transform: 'translateX(-50%)',
    zIndex: theme.zIndex.modal - 1,
  },
}));

type AlertKey = 'past-due-invoices' | 'open-orders' | 'trial-expiring' | 'ai-usage';

interface AlertConfig {
  actionLabel: string;
  key: AlertKey;
  message: string;
  onAction: () => void;
  priority: number;
  severity: NonNullable<AlertProps['severity']>;
}

export function UserAlerts() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const currentUser = useSelectCurrentUser();
  const [createCloseOrderUrl] = Apollo.useCreateCloseOrderUrlMutation();
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const isAtEditorRoute = useMatch({ end: false, path: routes.DocumentDetail });

  const handleDismiss = useCallback((key: string) => {
    setDismissedKeys(prev => new Set(prev).add(key));
  }, []);

  const alerts = useMemo<AlertConfig[]>(() => {
    if (!currentUser || isAtEditorRoute) return [];

    const result: AlertConfig[] = [];
    const subscription = currentUser.org?.alineSubscription;

    // Priority 2: No subscription, trial expiring or expired
    if (!subscription) {
      const trialEnd = parseISO(currentUser.org.trialEndAt);
      const daysRemaining = differenceInDays(trialEnd, new Date());

      result.push({
        actionLabel: 'Subscribe',
        key: 'trial-expiring',
        message: `Your trial ${getTrialTimeLabel(daysRemaining)}. Subscribe to keep access to all features.`,
        onAction: openCheckoutDialog,
        priority: 2,
        severity: daysRemaining <= 0 ? 'warning' : 'info',
      });
    } else {
      // Priority 2: Past-due invoices
      if (subscription.pastDueInvoices.length) {
        const count = subscription.pastDueInvoices.length;

        result.push({
          actionLabel: 'View Billing',
          key: 'past-due-invoices',
          message:
            count === 1
              ? 'You have a past-due invoice that requires attention.'
              : `You have ${count} past-due invoices that require attention.`,
          onAction: () => navigate(link.AdminBilling()),
          priority: 2,
          severity: 'error',
        });
      }

      // Priority 3: Open orders
      if (subscription.openOrders.length) {
        const count = subscription.openOrders.length;

        result.push({
          actionLabel: 'Complete Checkout',
          key: 'open-orders',
          message:
            count === 1
              ? 'You have an open order waiting to be completed.'
              : `You have ${count} open orders waiting to be completed.`,
          onAction: async () => {
            const openOrder = subscription.openOrders.at(0);
            if (!openOrder) return navigate(link.AdminBilling());

            const { data } = await createCloseOrderUrl({ variables: { orderId: openOrder.id } });
            if (data?.createCloseOrderUrl) window.location.href = data.createCloseOrderUrl;
            return;
          },
          priority: 3,
          severity: 'warning',
        });
      }
    }

    // Priority 4: AI usage threshold
    const percentageTokensConsumed = Math.round(
      (currentUser.aiUsage.usedTokens / currentUser.aiUsage.totalAvailableTokens) * 100
    );
    if (percentageTokensConsumed >= 75) {
      result.push({
        actionLabel: 'View Usage',
        key: 'ai-usage',
        message: `You have consumed ${percentageTokensConsumed}% of your AI tokens.`,
        onAction: () => navigate(link.AdminUsage()),
        priority: 4,
        severity: percentageTokensConsumed >= 100 ? 'error' : 'warning',
      });
    }

    return result.sort((a, b) => b.priority - a.priority);
  }, [createCloseOrderUrl, currentUser, isAtEditorRoute, navigate]);

  const visibleAlerts = useMemo(
    () => alerts.filter(alert => !dismissedKeys.has(alert.key)),
    [alerts, dismissedKeys]
  );

  if (visibleAlerts.length === 0) return null;

  return (
    <div className={classes.container}>
      {visibleAlerts.map(alert => (
        <AlineAlert
          key={alert.key}
          action={
            <div className={classes.alertActionContainer}>
              <Typography noWrap className={classes.boldAlertText} onClick={alert.onAction}>
                {alert.actionLabel}
              </Typography>
              <SmallCloseIconButton color="inherit" onClick={() => handleDismiss(alert.key)} />
            </div>
          }
          severity={alert.severity}
          style={{ pointerEvents: 'auto' }}
          variant="standard"
        >
          <Typography noWrap>{alert.message}</Typography>
        </AlineAlert>
      ))}
    </div>
  );
}

function getTrialTimeLabel(daysRemaining: number) {
  if (daysRemaining < 0) return 'has expired';
  if (daysRemaining === 0) return 'expires today';
  return `expires in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'}`;
}
